import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  useTheme,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  AdsClick as AdsClickIcon,
  AutoFixHigh as AutoFixHighIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface FeatureCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  actionLabel: string;
  actionPath?: string;
  comingSoon?: boolean;
}

const FeatureCardItem = ({
  title,
  description,
  icon,
  actionLabel,
  actionPath,
  comingSoon = false,
}: FeatureCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleClick = () => {
    if (actionPath && !comingSoon) {
      navigate(actionPath);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
        border: 'none',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        sx={{
          padding: '25px !important',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${theme.palette.primary.main}15`,
              color: theme.palette.primary.main,
            }}
          >
            {icon}
          </Box>
          {comingSoon && (
            <Chip
              label="Coming Soon"
              size="small"
              sx={{
                bgcolor: theme.palette.warning.main,
                color: 'white',
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 1.5,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, flex: 1 }}
        >
          {description}
        </Typography>

        <Button
          variant={comingSoon ? 'outlined' : 'contained'}
          endIcon={<ArrowForwardIcon />}
          onClick={handleClick}
          disabled={comingSoon}
          fullWidth
        >
          {actionLabel}
        </Button>
      </CardContent>
    </Card>
  );
};

const FeatureCards = () => {
  const features: FeatureCardProps[] = [
    {
      title: 'Campaign Analytics',
      description:
        'Detailed campaign performance analysis with daily breakdowns and Facebook metrics.',
      icon: <BarChartIcon fontSize="large" />,
      actionLabel: 'View Analytics',
      actionPath: '/reports',
    },
    {
      title: 'Ads Manager',
      description:
        'Manage individual ads, view creative performance, and optimize ad spend.',
      icon: <AdsClickIcon fontSize="large" />,
      actionLabel: 'Manage Ads',
      actionPath: '/reports',
    },
    {
      title: 'Optimizer',
      description:
        'AI-powered campaign optimization and budget recommendations.',
      icon: <AutoFixHighIcon fontSize="large" />,
      actionLabel: 'Coming Soon',
      comingSoon: true,
    },
  ];

  return (
    <Grid container spacing={3}>
      {features.map((feature, index) => (
        <Grid item xs={12} md={4} key={index}>
          <FeatureCardItem {...feature} />
        </Grid>
      ))}
    </Grid>
  );
};

export default FeatureCards;
